import { Box } from "@rebass/grid";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/reduxV2/broker/broker.hooks";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { parseIncomeRatioRanges } from "@rentiohq/shared-frontend/dist/utils/rentmatch.utils";
import {
  Banner,
  Button,
  ESpacings,
  Loading,
  MultiStepForm,
} from "@rentiohq/web-shared/dist/components";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import createSchema from "../../../forms/rentmatchScoreBroker";
import {
  isPerformingSubmitActionSelector,
  performingSubmitActionErrorSelector,
  performingSubmitActionResultSelector,
  resetActionCreator,
  submitActionCreator,
} from "../../../forms/rentmatchScoreBroker/schema.rentmatchScoreBroker.actions";
import { StandardIncomeScale } from "./components/StandardIncomeScale";

const formId = generateFormId();

/**
 * What the office scores candidates on. A scoring rule rather than a setting of
 * the tenant profile, which is why it has a tab of its own rather than sitting
 * under the intake mail and the flow choice.
 *
 * An office that has set nothing is shown the built-in scale as it really is: a
 * slope, written out at a few rents. The editable table of bands appears only
 * once the office asks for it, because bands are an approximation of that slope
 * and showing them as the current rule would be showing something the scoring
 * does not use.
 */
const RentmatchScorePreferences = () => {
  const { user, broker } = authHooks.useSelf();
  const dispatch = useDispatch();

  // Fetched here rather than relied upon, and fetched again on every visit.
  // useSelf only reads the store, so a page opened straight from the address
  // bar showed a spinner that never stopped: nothing on the way to it had asked
  // for the office record.
  //
  // The write still goes to the older API, which is the only one with a broker
  // write endpoint, so a save does not reach this copy by itself. Hence the
  // read back once the write settles, below.
  const { refetch } = brokerHooks.useDetail({
    shouldRefetch: true,
    id: user?.brokerId,
  });

  // A form is built from the stored table when it mounts and keeps what it was
  // opened with, so when the stored table changes it has to be built again.
  // Remounted rather than given a new id: a form registers itself under its id
  // on mount, and a new id leaves the page with nothing on it but the buttons.
  // Rather than read back right after firing off a write, which would race it,
  // the fetch waits for the write to settle.
  const isSaving = useSelector((state: any) =>
    user?.brokerId
      ? isPerformingSubmitActionSelector(user.brokerId)(state)
      : false,
  );
  const wasSaving = useRef(false);

  useEffect(() => {
    if (wasSaving.current && !isSaving) {
      refetch();
    }

    wasSaving.current = isSaving;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaving]);

  const [formVersion, setFormVersion] = useState(0);
  const [isPersonalizing, setIsPersonalizing] = useState(false);
  const storedRanges = JSON.stringify(broker?.rentmatchIncomeRanges ?? null);

  useEffect(() => {
    setFormVersion(version => version + 1);
  }, [storedRanges]);

  if (!broker) {
    return <Loading />;
  }

  const hasOwnTable = !!parseIncomeRatioRanges(broker.rentmatchIncomeRanges);

  // Back to the standard, and back to the view that shows it. Only writes when
  // there is something to clear: an office that opened the table but never
  // saved has nothing stored, and a needless write would say otherwise.
  const handleReset = () => {
    if (hasOwnTable) {
      dispatch(resetActionCreator(broker.id));
    }

    setIsPersonalizing(false);

    showAlert({
      type: "success",
      message: getLocalizedText("broker.rentmatch.income_ranges.reset.alert"),
    });
  };

  return (
    <>
      {/* In the scene rather than as a field of the form, which is how every
          other settings tab does it, and where the room below it comes from. */}
      <Box mb={ESpacings.loose}>
        <Banner
          hasDismiss={false}
          icon="gaugeDashboard"
          title={getLocalizedText("broker.rentmatch.income_ranges.info")}
        />
      </Box>

      {hasOwnTable || isPersonalizing ? (
        <MultiStepForm
          key={formVersion}
          formId={`update-broker-rentmatch-score-${formId}`}
          schemas={createSchema({ broker })}
          withAside={false}
          isPerformingSubmitActionSelector={isPerformingSubmitActionSelector(
            broker.id,
          )}
          performingSubmitActionResultSelector={performingSubmitActionResultSelector(
            broker.id,
          )}
          performingSubmitActionErrorSelector={performingSubmitActionErrorSelector(
            broker.id,
          )}
          submitActionCreator={submitActionCreator(broker.id)}
          submitLabel={getLocalizedText("system.save")}
          secondaryActions={
            <Button onClick={handleReset}>
              {getLocalizedText("broker.rentmatch.income_ranges.reset")}
            </Button>
          }
        />
      ) : (
        <>
          <StandardIncomeScale />
          <Box mt={ESpacings.loose}>
            <Button
              appearance="primary"
              onClick={() => setIsPersonalizing(true)}
            >
              {getLocalizedText("broker.rentmatch.income_ranges.personalize")}
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default RentmatchScorePreferences;
